// Journey-Stage Adaptive Dashboards — the portal-native dashboard ENGINE.
//
// Architecture (recipe → catalog → per-widget frame → grid):
//
//   • A WIDGET is a React component that owns its OWN data fetch via
//     `useWidgetData` and renders inside a `WidgetFrame`. The frame owns the
//     widget's loading / degraded / error / retry presentation and wraps the
//     body in an error boundary, so one widget failing (fetch OR render)
//     degrades ONLY its own card — the Home page, journey spine, and hero
//     always render. This per-widget independence is the fix for the
//     all-or-nothing `Promise.all` fan-outs the first release shipped.
//
//   • A RECIPE (`PortalHomeRecipe`) is the per-account-type ordered, stage-
//     gated catalog of widgets. `PortalHomeDashboard` filters the recipe by
//     the current stage (plus each widget's dynamic `hidden` predicate) and
//     lays the survivors out in the shared responsive `DashboardGrid`.
//     IMPORTANT: a spec's `render` must return an ELEMENT whose component
//     owns any hooks (never call hooks inside `render` itself) — the engine
//     mounts/unmounts whole widget components as stages change.
//
//   • `PortalHomeLayout` standardizes the page chrome every portal Home
//     shares: root rhythm (`space-y-space-5`), the primary-fetch loading
//     skeleton, and the primary-fetch error state with retry. The PRIMARY
//     fetch is the cheap stage/hero payload (`/home`, readiness,
//     qualification, dashboard); everything heavier belongs in widgets.
//
// Portal isolation: this file must never import the staff `@/dashboard/*`
// engine — `portal/isolation.test.ts` forbids the prefix. Presentational
// pieces live in `./DashboardPrimitives`; this file adds only composition
// and state machinery.

import {
  Component,
  Fragment,
  useCallback,
  useEffect,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { Button } from "@/design-system/components/ui";
import { PortalApiError } from "../api/client";
import { DashboardCard, DashboardGrid } from "./DashboardPrimitives";

// ── Per-widget data ──────────────────────────────────────────────────────────

export type WidgetDataStatus = "loading" | "error" | "ready";

export interface WidgetData<T> {
  status: WidgetDataStatus;
  /** Last successfully loaded value (kept through a failed reload). */
  data: T | null;
  error: string | null;
  reload: () => void;
}

export function widgetErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Cancel-guarded fetch state machine for a single widget. Re-runs when
 *  `deps` change; `reload` re-runs it in place. No React Query — this is the
 *  portal's plain `useEffect`+`useState` contract with a retry affordance. */
export function useWidgetData<T>(
  fetcher: () => Promise<T>,
  deps: readonly unknown[] = [],
): WidgetData<T> {
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<{
    status: WidgetDataStatus;
    data: T | null;
    error: string | null;
  }>({ status: "loading", data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, status: "loading", error: null }));
    fetcher().then(
      (data) => {
        if (!cancelled) setState({ status: "ready", data, error: null });
      },
      (err: unknown) => {
        if (!cancelled) {
          setState((s) => ({
            status: "error",
            data: s.data,
            error: widgetErrorMessage(err, "Couldn't load this right now."),
          }));
        }
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps is the caller's dependency contract; nonce drives reload.
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { status: state.status, data: state.data, error: state.error, reload };
}

// ── Degraded state ───────────────────────────────────────────────────────────

/** What a widget shows when its own fetch (or render) failed. Muted, honest,
 *  retryable — never a fake zero, never a blank page. */
export function WidgetDegradedNotice({
  message,
  onRetry,
}: {
  message?: string | null;
  onRetry?: () => void;
}) {
  return (
    <div role="alert" className="space-y-space-2">
      <p className="text-small text-gray-mid m-0">
        {message || "Couldn't load this right now."}
      </p>
      {onRetry && (
        <Button variant="text" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

// ── Error boundary ───────────────────────────────────────────────────────────

interface BoundaryProps {
  title?: string;
  span?: "full" | "wide";
  testId?: string;
  children: ReactNode;
}

/** Catches a widget's RENDER failure and degrades it to its own card, so a
 *  throwing widget can never blank the whole Home. */
export class WidgetErrorBoundary extends Component<BoundaryProps, { failed: boolean }> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- surfacing a swallowed widget crash for diagnostics.
    console.error("Portal dashboard widget failed to render", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.failed) {
      return (
        <DashboardCard title={this.props.title} span={this.props.span} testId={this.props.testId}>
          <WidgetDegradedNotice />
        </DashboardCard>
      );
    }
    return this.props.children;
  }
}

// ── Widget frame ─────────────────────────────────────────────────────────────

/** The per-widget frame: title/action chrome, loading skeleton, degraded
 *  state with retry, and an error boundary around the body. Pass the
 *  widget's own `useWidgetData` result as `state`; widgets that render from
 *  an already-loaded primary payload may omit it (they still get the
 *  boundary). */
export function WidgetFrame({
  title,
  action,
  span,
  state,
  className,
  children,
  testId,
}: {
  title?: string;
  action?: ReactNode;
  span?: "full" | "wide";
  state?: Pick<WidgetData<unknown>, "status" | "error" | "reload">;
  className?: string;
  children?: ReactNode;
  testId?: string;
}) {
  return (
    <WidgetErrorBoundary title={title} span={span} testId={testId}>
      <DashboardCard
        title={title}
        action={state?.status === "error" ? undefined : action}
        span={span}
        loading={state?.status === "loading"}
        className={className}
        testId={testId}
      >
        {state?.status === "error" ? (
          <WidgetDegradedNotice message={state.error} onRetry={state.reload} />
        ) : (
          children
        )}
      </DashboardCard>
    </WidgetErrorBoundary>
  );
}

// ── Recipe + catalog ─────────────────────────────────────────────────────────

export interface PortalWidgetSpec<Ctx> {
  /** Stable key; also the React key in the grid. */
  key: string;
  /** Render only when the current stage is in this list. Omit = every stage. */
  stages?: readonly string[];
  /** Dynamic hide (e.g. "only when the seller holds a note"). Hidden widgets
   *  render nothing — no empty frame. */
  hidden?: (ctx: Ctx) => boolean;
  /** Must return an ELEMENT (e.g. `<PipelineWidget ctx={ctx} />`) whose
   *  component owns its hooks — see the module header. */
  render: (ctx: Ctx) => ReactNode;
}

export interface PortalHomeRecipe<Ctx> {
  widgets: readonly PortalWidgetSpec<Ctx>[];
}

/** Filters the recipe by stage + `hidden`, then lays the widgets out in the
 *  shared responsive grid. Returns null when nothing is visible so callers
 *  can skip the grid entirely at teaching-stage heroes. */
export function PortalHomeDashboard<Ctx>({
  recipe,
  ctx,
  stage,
  testId,
}: {
  recipe: PortalHomeRecipe<Ctx>;
  ctx: Ctx;
  stage: string;
  testId?: string;
}) {
  const visible = recipe.widgets.filter(
    (w) => (!w.stages || w.stages.includes(stage)) && !w.hidden?.(ctx),
  );
  if (visible.length === 0) return null;
  return (
    <DashboardGrid data-testid={testId}>
      {visible.map((w) => (
        <Fragment key={w.key}>{w.render(ctx)}</Fragment>
      ))}
    </DashboardGrid>
  );
}

// ── Page layout ──────────────────────────────────────────────────────────────

/** Standard page chrome for every portal Home: one vertical rhythm, one
 *  primary-fetch loading skeleton, one primary-fetch error presentation with
 *  retry. Only the PRIMARY (stage/hero) fetch may gate the whole page —
 *  everything else degrades per-widget inside its frame. */
export function PortalHomeLayout({
  loading,
  error,
  onRetry,
  stage,
  testId,
  children,
}: {
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  stage?: string;
  testId?: string;
  children?: ReactNode;
}) {
  if (error) {
    return (
      <div data-testid={testId} className="space-y-space-2">
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
        {onRetry && (
          <Button variant="text" size="sm" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    );
  }
  if (loading) {
    return (
      <div data-testid={testId} className="space-y-space-5">
        <DashboardGrid>
          <DashboardCard span="full" loading testId={testId ? `${testId}-loading` : undefined} />
          <DashboardCard loading />
          <DashboardCard loading />
        </DashboardGrid>
      </div>
    );
  }
  return (
    <div data-testid={testId} data-stage={stage} className="space-y-space-5">
      {children}
    </div>
  );
}
